import { AfterViewInit, Directive, ElementRef, HostListener, OnDestroy, inject, input } from '@angular/core';

/**
 * Anima a altura de um corpo rolável quando o seu conteúdo muda de tamanho.
 *
 * <p>Existe porque `height: auto` não se anima: sem uma altura em pixéis, trocar de separador
 * num painel faz o painel saltar de tamanho. A diretiva mede o conteúdo e escreve a altura; a
 * transição em si vive no CSS de quem a usa (`transition: height …`).
 *
 * <p>Aplica-se ao elemento do corpo, passando-lhe a superfície (para saber quanto espaço há) e
 * o invólucro do conteúdo (cuja altura natural é o alvo).
 *
 * @example
 * <div class="painel" #superficie>
 *   <header>…</header>
 *   <div class="corpo" [appAutoHeight]="superficie" [appAutoHeightContent]="conteudo">
 *     <div #conteudo>…</div>
 *   </div>
 * </div>
 */
@Directive({
  selector: '[appAutoHeight]',
})
export class AutoHeight implements AfterViewInit, OnDestroy {
  /** Superfície do painel — dá o teto de altura disponível. */
  readonly surface = input.required<HTMLElement>({ alias: 'appAutoHeight' });
  /** Invólucro cuja altura natural determina a do corpo. */
  readonly content = input.required<HTMLElement>({ alias: 'appAutoHeightContent' });
  /** Quanto da janela a superfície pode ocupar. */
  readonly maxRatio = input(0.94, { alias: 'appAutoHeightMax' });

  private readonly body = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private observer?: ResizeObserver;
  private last = -1;

  ngAfterViewInit(): void {
    if (typeof ResizeObserver === 'undefined') return;
    this.observer = new ResizeObserver(() => this.sync());
    this.observer.observe(this.content());
    this.sync(true); // a primeira vez entra sem animação: não há de onde animar
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  @HostListener('window:resize')
  protected onWindowResize(): void {
    this.sync();
  }

  /**
   * Recalcula já, sem esperar pelo observador.
   *
   * <p>O `ResizeObserver` é assíncrono, e há quem precise da altura final dentro da mesma tarefa
   * — o painel, ao voltar do canto, mede-se para animar. Nesse instante o corpo acabou de deixar
   * de estar escondido e ainda tem altura zero: medir dali dava um destino errado.
   */
  syncNow(): void {
    this.sync(true);
  }

  private sync(immediate = false): void {
    const available = window.innerHeight * this.maxRatio() - this.chromeHeight();
    const target = Math.round(Math.min(this.content().offsetHeight, Math.max(80, available)));
    if (Math.abs(target - this.last) < 1) return;
    this.last = target;

    if (immediate) {
      this.body.style.transition = 'none';
      this.body.style.height = `${target}px`;
      void this.body.offsetHeight; // leitura forçada: sem ela a transição desligada não "pega"
      this.body.style.transition = '';
    } else {
      this.body.style.height = `${target}px`;
    }
  }

  /**
   * Altura de tudo o que não é o corpo: cabeçalho, rodapé, separadores, e os espaçamentos pelo
   * caminho.
   *
   * <p>Sobe da superfície até ao corpo somando os irmãos de cada nível, em vez de fazer
   * "superfície − corpo": a superfície tem `max-height`, por isso quando o conteúdo é grande ela
   * satura e aquela subtração devolvia uma moldura menor que a real — o corpo ficava alto demais
   * e empurrava o rodapé para fora.
   */
  private chromeHeight(): number {
    let chrome = 0;
    let node: HTMLElement = this.body;
    const surface = this.surface();
    while (node !== surface && node.parentElement) {
      const parent = node.parentElement;
      for (const sibling of Array.from(parent.children)) {
        if (sibling === node) continue;
        const position = getComputedStyle(sibling as HTMLElement).position;
        // Irmãos fora do fluxo não ocupam espaço; contá-los inflava a moldura.
        if (position === 'absolute' || position === 'fixed') continue;
        chrome += (sibling as HTMLElement).offsetHeight;
      }
      chrome += AutoHeight.frameOf(parent);
      node = parent;
    }
    return chrome;
  }

  private static frameOf(element: HTMLElement): number {
    const styles = getComputedStyle(element);
    return parseFloat(styles.paddingTop || '0')
      + parseFloat(styles.paddingBottom || '0')
      + parseFloat(styles.borderTopWidth || '0')
      + parseFloat(styles.borderBottomWidth || '0');
  }
}
